/**
 * Monkey Worlds — authored level tilemaps (client-owned; the server only validates indices).
 *
 * Legend (spec AC-8):
 *   '#' solid ground · '=' one-way platform · 'B' breakable block · 'W' charge-wall (Gridiron
 *   verb only) · 'c' coin · 'e' patrol enemy · 'f' flying enemy (sine hover) · 'p' pitcher hazard
 *   (Diamond) · '^' spikes · 'h' hurdle (blocks running, not sliding) · 'R' rope anchor (hangs a
 *   swing rope) · 'T' spring · 'J' jetpack pickup · 'K' checkpoint · 'S' spawn · 'F' flag/goal ·
 *   ' ' air. Rows are right-trimmed; the parser pads to the widest row. Entities are stripped
 *   from the collision grid ('h' stays — it collides conditionally via hurdleBlocks()).
 *
 * Levels may also declare moving platforms (lifts): deterministic sine paths via
 * `platforms: [{ x, y, w, axis, range, period }]` (tile units / ticks; see platformPosAt()).
 *
 * Feel guarantees the maps are authored against (MONKEY_WORLDS_PHYSICS): regular jump apex
 * ~3.6 tiles, double jump adds ~3 more, charged Hoops bounce ~8, spring bounce ~9.6, safe
 * run-jump gap ≤ 6 tiles, rope length 4. Slot 6 of each world is the boss/gauntlet that requires
 * the world verb; earlier slots ramp difficulty.
 */

export interface MonkeyWorldsPlatform {
  /** Base position of the platform's left edge (tile units). */
  x: number;
  y: number;
  /** Width in tiles. */
  w: number;
  axis: 'x' | 'y';
  /** Total travel span in tiles (± range/2 around the base). */
  range: number;
  /** Full cycle length in ticks. */
  period: number;
}

export interface MonkeyWorldsLevel {
  /** World index 0-3 (canonical order of MONKEY_WORLDS registry). */
  world: number;
  /** Slot 1-6 within the world. */
  slot: number;
  /** Par time for the 3-star feat, in 60 Hz ticks. */
  timeParTicks: number;
  /** Boss/gauntlet level (always slot 6). */
  boss: boolean;
  rows: string[];
  platforms: MonkeyWorldsPlatform[];
}

const SKY = '';

interface RawLevel {
  rows: string[];
  platforms?: MonkeyWorldsPlatform[];
}

/** world 0 — Football (kick) ------------------------------------------------------------- */

const FOOTBALL_LEVELS: RawLevel[] = [
  // slot 1 — first steps: small pit, spring up to the ledge, one B gate, a hurdle to slide
  {
    rows: [
      SKY,
      SKY,
      SKY,
      SKY,
      SKY,
      '                 ccc',
      '                =====',
      '                                          cc',
      '       cc                                ====        B',
      '      ====     T    e         K          c           B    h     e        F',
      ' S              #########        ccc          ###############################',
      '######   ###################    #####     ###################################',
      '######   ###################    #####     ###################################',
      '######^^^###################^^^^#####^^^^^###################################',
    ],
  },
  // slot 2 — stacked B gates + rope over the long spike strip
  {
    rows: [
      SKY,
      SKY,
      '                            R',
      '                                  ccc',
      '                                 =====',
      '            cc                                       cc',
      '           ====        B               c  e         ====      B',
      '                       B         ====                         B      f',
      '   S         e         B    cc         K        e             B      e     F',
      '#################################==########################################',
      '############################################################################',
      '########^^^^^^^^############^^^^^^^^^^^^^^^^################^^^^^^##########',
      '############################################################################',
      '############################################################################',
    ],
  },
  // slot 3 — pit rhythm with coin arcs + a lift over the middle gap
  {
    rows: [
      SKY,
      SKY,
      '                        ccc',
      '                       =====',
      '          cc                        cc          ccc',
      '         ====          e           ====        =====        B',
      '                   ========                                 B        e',
      '   S        e   T             K          e         c        B              F',
      '#########      ##########       #############      ########################',
      '#########      ##########       #############      ########################',
      '#########^^^^^^##########^^^^^^^#############^^^^^^########################',
      '############################################################################',
      '############################################################################',
      '############################################################################',
    ],
    platforms: [{ x: 26, y: 6.5, w: 3, axis: 'y', range: 3, period: 240 }],
  },
  // slot 4 — vertical B tower detour, hurdles on the flats, a flyer patrol
  {
    rows: [
      SKY,
      '                                             ccccc',
      '                                            =======',
      '                     cc                                    cc',
      '                    ====         B          f             ====',
      '        e                        B          e                       B',
      '      =====          e           B        =====         e           B   e',
      '  S       h      e       K       B             h                h   B      F',
      '##########################################################################',
      '#########^^^^^^^^^^^^#####################^^^^^^^^^^^^^^^################',
      '##########################################################################',
      '##########################################################################',
      '##########################################################################',
      '##########################################################################',
    ],
  },
  // slot 5 — long gaps, rope crossing, jetpack vault behind B
  {
    rows: [
      SKY,
      '                              R',
      '                                                        ccc',
      '              ccc                                      =====',
      '             =====             B ccc B',
      '                               B==J==B                              cc',
      '       cc            e         B     B         e         e        ====',
      '  S   ====      e        K     B     B             c                      F',
      '###########      #########    #########    ##############################',
      '###########      #########    #########    ##############################',
      '###########^^^^^^#########^^^^#########^^^^##############################',
      '##########################################################################',
      '##########################################################################',
      '##########################################################################',
    ],
  },
  // slot 6 — BOSS: kick gauntlet — three B gates, enemy waves, springs and checkpoints
  {
    rows: [
      SKY,
      '                 ccc                    ccc                    ccc',
      '                =====                  =====                  =====',
      '                          B                      B                      B',
      '          e               B          e           B          e           B',
      '        =====             B        =====         B        =====         B',
      '   S       T   e    e     B  K           e  e    B  K  T        e  e    B F',
      '##########################################################################',
      '#######^^^^^^^^^^^^^^^############^^^^^^^^^^^^^^^#########^^^^^^^^^^^####',
      '##########################################################################',
      '##########################################################################',
      '##########################################################################',
      '##########################################################################',
      '##########################################################################',
    ],
  },
];

/** world 1 — Hoops (bounce: charged super-jump + down-slam; springs everywhere) ------------ */

const HOOPS_LEVELS: RawLevel[] = [
  // slot 1 — learn the charged jump + spring: a high ledge two ways up
  {
    rows: [
      SKY,
      SKY,
      '                              ccc                                    F',
      '                             =====                            ########',
      '                                                       cc     #',
      '                                                      ====    #',
      '        cc                ####BBBB#####                       #',
      '       ====               #                    e              #',
      '  S          T   e        #  K     ccc               T        #',
      '######################################################################',
      '######################################################################',
      '########^^^^^^^^^^########^^^^^^^^^^^^^^^^^^^^########################',
      '######################################################################',
      '######################################################################',
    ],
  },
  // slot 2 — learn the slam: B floors hide coin cellars, springs bounce you back out
  {
    rows: [
      SKY,
      SKY,
      SKY,
      '            cc                       cc                      cc',
      '           ====                     ====                    ====',
      '                       e                         e',
      '   S          T             K            f                T          F',
      '################BBBB#################BBBB############################',
      '#####           ccc     ############ ccc        #####################',
      '#####     e             ############       e    #####################',
      '#####################################################################',
      '#####################################################################',
      '#####################################################################',
      '#####################################################################',
    ],
  },
  // slot 3 — tower hop: rising ledges, vertical lift, slam shortcut down
  {
    rows: [
      '                                                            ccc      F',
      '                                                           ###########',
      '                                          cc               #',
      '                                         ####              #',
      '                        cc                                 #',
      '                       ####            e                   #',
      '        cc                                                 #',
      '       ####             e              BBBB                #',
      '  S       T        K                   cccc          T     #',
      '######################################################################',
      '###########^^^^^^^^^^^^^^^^^^^^^^^####################################',
      '######################################################################',
      '######################################################################',
      '######################################################################',
    ],
    platforms: [{ x: 30, y: 5.5, w: 3, axis: 'y', range: 4, period: 260 }],
  },
  // slot 4 — bounce over spike lakes, springs between islands
  {
    rows: [
      SKY,
      SKY,
      '                     ccc                         ccc',
      '                    =====                       =====',
      '                                    cc',
      '                                   ====                        cc',
      '        e                BB          f             e          ====',
      '  S       T         e         K          T                           F',
      '##########      ##############       #############      #############',
      '##########      ##############       #############      #############',
      '##########^^^^^^##############^^^^^^^#############^^^^^^#############',
      '######################################################################',
      '######################################################################',
      '######################################################################',
    ],
  },
  // slot 5 — double-decker: slam through, spring back out, jetpack cellar
  {
    rows: [
      SKY,
      '           ccc                                        ccc',
      '          =====                                      =====',
      '                          e                                        F',
      '   S          T                K        e                    #########',
      '###############BBBB###############BBBB###############       #',
      '#####  cc            e      ###### ccc          e    #      #',
      '#####  cc      J     T      ###### ccc      T         #      #',
      '#####################BBBB############################       #',
      '#########       cccc              e            ######################',
      '#########             e                        ######################',
      '#####################################################################',
      '#####################################################################',
      '#####################################################################',
    ],
  },
  // slot 6 — BOSS: slam-dunk gauntlet — B ceilings, spike pits, springs, high rim finish
  {
    rows: [
      '                                                                     F',
      '                                                              ########',
      '            ccc                    ccc                        #',
      '           =====                  =====                       #',
      '                        e                       e             #',
      '                      =====                   =====           #',
      '   S     T  e   e         K     e   e     T      K     e  e   #',
      '################BBBB#################BBBB####################',
      '#####    ccc         e      #####     ccc        e    ##############',
      '#####    ccc                #####     ccc              ##############',
      '#####################################################################',
      '#########^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^###################',
      '#####################################################################',
      '#####################################################################',
    ],
  },
];

/** world 2 — Diamond (swing: reflect pitches; batting-cage ropes) -------------------------- */

const DIAMOND_LEVELS: RawLevel[] = [
  // slot 1 — meet the pitcher: one lane, learn the reflect, a first rope
  {
    rows: [
      SKY,
      SKY,
      '                          R',
      SKY,
      '                 ccc',
      '                =====                                cc',
      '                                                    ====',
      '        cc                     c',
      '       ====          e     K        p                    e          F',
      '  S             ##########      ##########      ####################',
      '#########      ############    ############    #####################',
      '#########      ############    ############    #####################',
      '#########^^^^^^############^^^^############^^^^#####################',
      '#####################################################################',
    ],
  },
  // slot 2 — crossfire: pitchers on ledges over a coin run, rope escape
  {
    rows: [
      SKY,
      '                                   R',
      '              p                              p',
      '           =======                        =======',
      '                          ccc',
      '                         =====                          cc',
      '        cc                              e              ====',
      '   S   ====        e        K      c         f                e     F',
      '#####################################################################',
      '#########^^^^^^^^^^^^^^^^^^^############^^^^^^^^^^^^^################',
      '#####################################################################',
      '#####################################################################',
      '#####################################################################',
      '#####################################################################',
    ],
  },
  // slot 3 — spike alley with overhead pitching + fly balls, rope over the middle pit
  {
    rows: [
      SKY,
      '                       p              R              p',
      '                    =======                       =======',
      '            cc                        ccc',
      '           ====                      =====              cc',
      '                        e       f                      ====',
      '   S         e     K            e            e     c          e     F',
      '##########      ############       ############      ##############',
      '##########      ############       ############      ##############',
      '##########^^^^^^############^^^^^^^############^^^^^^##############',
      '####################################################################',
      '####################################################################',
      '####################################################################',
      '####################################################################',
    ],
  },
  // slot 4 — batting cages: enclosed lanes, reflect to clear, rope between cages
  {
    rows: [
      SKY,
      '                                R',
      '            ccc                          ccc',
      '           =====                        =====',
      '                     p         e                   p        e',
      '   S           K                    c                               F',
      '###########################==########################==#############',
      '#####   e     p       ##### cc #####    e     p      ###############',
      '#####                 ##### cc #####                 ###############',
      '####################################################################',
      '#########^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^################',
      '####################################################################',
      '####################################################################',
      '####################################################################',
    ],
  },
  // slot 5 — double pitch towers over long pits, ropes across, jetpack stash
  {
    rows: [
      SKY,
      '              p           R               R           p',
      '           =======                                 =======',
      '                            ccc        ccc',
      '                           =====      =====',
      '        cc                                              cc',
      '       ====         e         J    e             e     ====',
      '  S            K                                   c                F',
      '##########      ##############      #############      #############',
      '##########      ##############      #############      #############',
      '##########^^^^^^##############^^^^^^#############^^^^^^#############',
      '####################################################################',
      '####################################################################',
      '####################################################################',
    ],
  },
  // slot 6 — BOSS: the ace — three-pitcher gauntlet over a spike diamond, ropes to cross
  {
    rows: [
      SKY,
      '            p          R         p          R         p',
      '         =======              =======              =======',
      '                    ccc                   ccc',
      '                   =====                 =====',
      '        e   e               e   e                 e   e',
      '   S           K                     K       c                      F',
      '####################################################################',
      '#######^^^^^^^^^^^^^^^^^^############^^^^^^^^^^^^^^^^^^^############',
      '####################################################################',
      '####################################################################',
      '####################################################################',
      '####################################################################',
      '####################################################################',
    ],
  },
];

/** world 3 — Gridiron (charge: dash through W walls; hurdles everywhere) ------------------- */

const GRIDIRON_LEVELS: RawLevel[] = [
  // slot 1 — first wall + first hurdles: charge or slide
  {
    rows: [
      SKY,
      SKY,
      SKY,
      SKY,
      '                 ccc',
      '                =====                                  cc',
      '                                                      ====',
      '        cc                     W',
      '       ====       h  e     K   W        h   e            h    e     F',
      '  S                            W            c',
      '####################################################################',
      '#########^^^^^^^^^^^^^^^^^^^############^^^^^^^^^^^^################',
      '####################################################################',
      '####################################################################',
    ],
  },
  // slot 2 — wall rhythm + hurdles + patrols between
  {
    rows: [
      SKY,
      SKY,
      '              ccc                         ccc',
      '             =====                       =====',
      '                        W                           W',
      '        cc              W          e                W          cc',
      '       ====     e       W      K             e      W         ====',
      '  S        h            W          h    c           W     h         F',
      '####################################################################',
      '####################################################################',
      '########^^^^^^^^^^^^############^^^^^^^^^^^^^^^^####################',
      '####################################################################',
      '####################################################################',
      '####################################################################',
    ],
  },
  // slot 3 — dash the gaps: walls on ledges over pits, a horizontal lift ferry
  {
    rows: [
      SKY,
      SKY,
      '                         ccc                    ccc',
      '                        =====                  =====',
      '             W                       W',
      '        cc   W                       W               e        cc',
      '       ====  W        e         K    W        f              ====',
      '  S          W            h          W             c    h           F',
      '##########      ###############      ############      #############',
      '##########      ###############      ############      #############',
      '##########^^^^^^###############^^^^^^############^^^^^^#############',
      '####################################################################',
      '####################################################################',
      '####################################################################',
    ],
    platforms: [{ x: 53, y: 7.5, w: 3, axis: 'x', range: 4, period: 280 }],
  },
  // slot 4 — the blitz: enemy lines behind walls, hurdle chains
  {
    rows: [
      SKY,
      SKY,
      '            ccc                            ccc',
      '           =====                          =====',
      '                      W                              W',
      '                      W        e  e                  W       e  e',
      '   S        e     K   W    h            c    e       W   h          F',
      '####################################################################',
      '#######^^^^^^^^^^^############^^^^^^^^^^^^^^^^^^^############^^^####',
      '####################################################################',
      '####################################################################',
      '####################################################################',
      '####################################################################',
      '####################################################################',
    ],
  },
  // slot 5 — trench run: low ceiling, stacked walls, hurdles, jetpack trench
  {
    rows: [
      SKY,
      '                                                        ccc',
      '              ccc                                      =====',
      '             =====             W     W',
      '                               W ccc W                         cc',
      '        cc           e         W  J  W        e       e       ====',
      '  S    ====      K             W     W            h           h     F',
      '###########      #########    #########    ########################',
      '###########      #########    #########    ########################',
      '###########^^^^^^#########^^^^#########^^^^########################',
      '####################################################################',
      '####################################################################',
      '####################################################################',
      '####################################################################',
    ],
  },
  // slot 6 — BOSS: goal-line stand — four wall gates, blitzing patrols, hurdles, spike finale
  {
    rows: [
      SKY,
      '                ccc                  ccc                  ccc',
      '               =====                =====                =====',
      '                        W                     W                     W',
      '          e             W          e          W          e          W',
      '        =====           W        =====        W        =====        W',
      '   S       h  e    e    W  K       h   e  e   W  K       h  e  e   W F',
      '####################################################################',
      '######^^^^^^^^^^^^^^^^^^^^###########^^^^^^^^^^^^^^^^^^^############',
      '####################################################################',
      '####################################################################',
      '####################################################################',
      '####################################################################',
      '####################################################################',
    ],
  },
];

const WORLD_MAPS: RawLevel[][] = [FOOTBALL_LEVELS, HOOPS_LEVELS, DIAMOND_LEVELS, GRIDIRON_LEVELS];

/** Par ticks scale with map width: generous ~8 tiles/s effective pace at 60 Hz. */
function parTicksForWidth(width: number): number {
  return Math.round(width * 14);
}

/**
 * Rows of clear sky required above the goal row. The tallest goal structure is the basketball
 * backboard (MONKEY_WORLDS_GOAL.BOARD_TOP = 4.7 tiles above the goal's ground point), so a flag
 * authored near the top of a map would put its rim off the level. Maps are padded to guarantee it
 * rather than requiring every author to remember — see GOAL_HEADROOM_ROWS in the tests.
 */
export const GOAL_HEADROOM_ROWS = 6;

/**
 * Characters that must rest ON a surface. Authored maps put these on whichever row read nicely in
 * the ASCII, which left hurdles, enemies, checkpoints and even the goal floating a tile above the
 * floor (and a floating hurdle never collides). Rather than hand-align 24 maps, every one of these
 * is snapped down onto the first surface beneath it. `c` coins, `f` flyers, `R` ropes and `J`
 * jetpacks are deliberately excluded — those are meant to hang in the air.
 */
const GROUND_ENTITIES = 'SFKTeph';
const SURFACES = '#BW=';

function snapGroundEntities(rows: string[]): string[] {
  const grid = rows.map((r) => r.split(''));
  const h = grid.length;
  const w = grid[0].length;

  /** Where would this entity land in column `cx`? Null if there's no usable footing there. */
  function landingIn(cx: number, fromY: number): number | null {
    if (cx < 0 || cx >= w) return null;
    for (let y2 = fromY + 1; y2 < h; y2++) {
      if (SURFACES.indexOf(grid[y2][cx]) < 0) continue;
      const target = y2 - 1;
      if (target < 0) return null;
      const occupant = grid[target][cx];
      // free, or a floating decoration we can swap upward — anything else (spikes, blocks) is
      // not a place to stand, so this column is unusable
      return occupant === ' ' || 'cfRJ'.indexOf(occupant) >= 0 ? target : null;
    }
    return null;
  }

  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) {
      const ch = grid[y][x];
      if (GROUND_ENTITIES.indexOf(ch) < 0) continue;
      // already resting on something — leave it (also stops re-processing what we just moved)
      if (y + 1 < h && SURFACES.indexOf(grid[y + 1][x]) >= 0) continue;
      // Prefer straight down; a few entities are authored over a pit (a checkpoint you could never
      // stand on), so widen to nearby columns before giving up.
      let col = -1;
      let target = -1;
      for (let dx = 0; dx <= 4 && col < 0; dx++) {
        for (const cx of dx === 0 ? [x] : [x - dx, x + dx]) {
          const land = landingIn(cx, y);
          if (land !== null) {
            col = cx;
            target = land;
            break;
          }
        }
      }
      if (col < 0 || (col === x && target === y)) continue;
      const occupant = grid[target][col];
      grid[target][col] = ch;
      grid[y][x] = occupant; // any displaced decoration takes the cell we vacated
    }
  }
  return grid.map((r) => r.join(''));
}

function flagRowOf(rows: string[]): number {
  for (let y = 0; y < rows.length; y++) if (rows[y].indexOf('F') >= 0) return y;
  return rows.length;
}

function buildLevels(): MonkeyWorldsLevel[] {
  const levels: MonkeyWorldsLevel[] = [];
  for (let world = 0; world < WORLD_MAPS.length; world++) {
    const maps = WORLD_MAPS[world];
    for (let i = 0; i < maps.length; i++) {
      const raw = maps[i];
      const width = Math.max(...raw.rows.map((r) => r.length));
      let padded = snapGroundEntities(raw.rows.map((r) => r.padEnd(width, ' ')));
      const missing = GOAL_HEADROOM_ROWS - flagRowOf(padded);
      if (missing > 0) {
        const sky = Array.from({ length: missing }, () => ' '.repeat(width));
        padded = sky.concat(padded);
      }
      const shift = Math.max(0, missing);
      levels.push({
        world,
        slot: i + 1,
        timeParTicks: parTicksForWidth(width),
        boss: i + 1 === 6,
        rows: padded,
        platforms: (raw.platforms ?? []).map((pl) => ({ ...pl, y: pl.y + shift })),
      });
    }
  }
  return levels;
}

export const MONKEY_WORLDS_LEVELS: readonly MonkeyWorldsLevel[] = buildLevels();

export function getMonkeyWorldsLevel(world: number, slot: number): MonkeyWorldsLevel | undefined {
  return MONKEY_WORLDS_LEVELS.find((l) => l.world === world && l.slot === slot);
}

/** Coins in a map (drives the all-coins star feat; asserted against the maps by T-7). */
export function countMonkeyWorldsCoins(rows: readonly string[]): number {
  let total = 0;
  for (const row of rows) {
    for (const ch of row) if (ch === 'c') total++;
  }
  return total;
}
